import { createDownloadLink, downloadFile } from '../../api/v2/FilesApi';
import { generateUrl } from '../../api/v2/ApiUtils';

// Saves a blob to the user's machine via a temporary object URL + anchor click.
// Shared so the various download spots don't each re-implement the anchor/URL dance.
export const saveBlob = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// Opens a download link directly. An attachment response doesn't leave the page, so an anchor
// click is enough, and the browser streams the file itself with its own save dialog and progress.
export const openDownload = (href: string): void => {
    const link = document.createElement('a');
    link.href = href;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// The server returns a root-relative link. A built site has an API base to put in front of it;
// in dev the API is proxied on this origin, so the path is used as-is.
const resolveApiHref = (rootRelative: string): string => {
    const resolved = generateUrl(rootRelative.replace(/^\//, ''));
    const href = typeof resolved === 'string' ? resolved : resolved.href;
    return /^https?:\/\//.test(href) ? href : rootRelative;
};

// Creates a signed link and opens it. The file arrives under the server's download name.
// Returns an error message on failure, else null.
export const downloadFileByLink = async (fileId: string, fileName: string): Promise<string | null> => {
    const result = await createDownloadLink(fileId);
    if (result?.error) return result.error.message;
    if (!result?.data?.url) return `Failed to download: ${fileName}`;
    openDownload(resolveApiHref(result.data.url));
    return null;
};

// Downloads a stored grant file by id and saves it. Returns an error message on failure, else null.
// With streaming on (configuration.streamingFileTransfer) the browser opens a signed link; off,
// the file is fetched as a blob and saved - the original behavior.
export const downloadFileById = async (fileId: string, fileName: string, streaming = false): Promise<string | null> => {
    if (streaming) return downloadFileByLink(fileId, fileName);

    const result = await downloadFile(fileId);
    if (result?.error) return result.error.message;
    if (!result?.data) return `Failed to download: ${fileName}`;
    saveBlob(result.data, fileName);
    return null;
};
